import type {
  UserId,
  InboxKind,
  InboxSourceKind,
  TaskId,
  ChannelId,
  MockCache,
  InboxItemId,
} from '../types';

import { newId } from './ids';
import { addToIndex } from './indexes';
import { takeInboxSeq } from './runtime';

export interface InboxWish {
  userId: UserId;
  kind: InboxKind;
  sourceKind: InboxSourceKind;
  sourceId: string;
  taskId: TaskId | null;
  channelId: ChannelId | null;
  preview: string;
  channelMuted?: boolean;
}

const KIND_PRIORITY: Record<InboxKind, number> = {
  mention: 0,
  chat_mention: 0,
  comment_reply: 1,
  assigned: 2,
  assigned_check: 2,
  assigned_comment: 2,
  comment: 3,
  chat_dm: 4,
  thread_reply: 4,
  status: 5,
  task_added: 6,
  task_changed: 6,
  group_added: 7,
  birthday: 7,
  reminder: 7,
};

function upsertInbox(
  cache: MockCache,
  wish: { userId: UserId; kind: InboxKind; sourceKind: InboxSourceKind; sourceId: string; taskId: TaskId | null; channelId: ChannelId | null; preview: string },
): void {
  const now = cache.nowMs();
  const owned = cache.index.inboxByUser.get(wish.userId) ?? [];
  for (const id of owned) {
    const item = cache.inbox.get(id);
    if (
      item !== undefined &&
      item.kind === wish.kind &&
      item.sourceKind === wish.sourceKind &&
      item.sourceId === wish.sourceId
    ) {
      item.state = 'unread';
      item.stateChangedAt = now;
      item.snoozedUntilAt = null;
      item.preview = wish.preview;
      return;
    }
  }
  const id = newId('inboxItem') as InboxItemId;
  cache.inbox.set(id, {
    id,
    seq: takeInboxSeq(),
    userId: wish.userId,
    kind: wish.kind,
    actorId: cache.me,
    sourceKind: wish.sourceKind,
    sourceId: wish.sourceId,
    taskId: wish.taskId,
    channelId: wish.channelId,
    state: 'unread',
    preview: wish.preview,
    createdAt: now,
    stateChangedAt: now,
    snoozedUntilAt: null,
  });
  addToIndex(cache, 'inboxItem', id);
}

export function deliver(cache: MockCache, actorId: UserId, wishes: InboxWish[]): void {
  const best = new Map<string, InboxWish>();
  for (const wish of wishes) {
    if (wish.userId === actorId) {
      continue;
    }
    if (wish.kind === 'chat_dm' && wish.channelMuted === true) {
      continue;
    }
    const key = `${String(wish.userId)}|${wish.sourceKind}|${wish.sourceId}`;
    const current = best.get(key);
    if (current === undefined || KIND_PRIORITY[wish.kind] < KIND_PRIORITY[current.kind]) {
      best.set(key, wish);
    }
  }
  for (const wish of best.values()) {
    upsertInbox(cache, wish);
  }
}
